import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as api from '../../api/AccountsAPI/verifyOpenFDAPI';

const err = (e, fallback) => e?.ErrorDetails?.ErrorMessage || e?.Message || e?.message || fallback;

export const fetchVerifyOpenFDQueue = createAsyncThunk(
    'verifyOpenFD/fetchQueue',
    async (roleId, { rejectWithValue }) => {
        try { return await api.getVerificationOpenFDs(roleId); }
        catch (e) { return rejectWithValue(err(e, 'Failed to fetch Open FD verification queue')); }
    }
);

export const fetchVerifyOpenFDDetail = createAsyncThunk(
    'verifyOpenFD/fetchDetail',
    async (transactionNo, { rejectWithValue }) => {
        try { return await api.getOpenFDByTransNo(transactionNo); }
        catch (e) { return rejectWithValue(err(e, 'Failed to fetch FD details')); }
    }
);

export const fetchOpenFDBanks = createAsyncThunk(
    'verifyOpenFD/fetchBanks',
    async (_, { rejectWithValue }) => {
        try { return await api.getBanks(); }
        catch (e) { return rejectWithValue(err(e, 'Failed to fetch banks')); }
    }
);

export const submitApproveOpenFD = createAsyncThunk(
    'verifyOpenFD/approve',
    async (payload, { rejectWithValue }) => {
        try { return await api.approveOpenFD(payload); }
        catch (e) { return rejectWithValue(err(e, 'Error Occurred')); }
    }
);

export const submitUpdateOpenFD = createAsyncThunk(
    'verifyOpenFD/update',
    async (payload, { rejectWithValue }) => {
        try { return await api.updateOpenFD(payload); }
        catch (e) { return rejectWithValue(err(e, 'Error Occurred While Updating OpenFD')); }
    }
);

const initialState = {
    queue: [], queueLoading: false, queueError: null,
    detail: null, detailLoading: false, detailError: null,
    banks: [],
    verifyLoading: false, updateLoading: false,
};

const verifyOpenFDSlice = createSlice({
    name: 'verifyOpenFD',
    initialState,
    reducers: {
        clearVerifyOpenFDDetail: (s) => { s.detail = null; s.detailError = null; s.detailLoading = false; },
        resetVerifyOpenFD: () => initialState,
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchVerifyOpenFDQueue.pending, (s) => { s.queueLoading = true; s.queueError = null; })
            .addCase(fetchVerifyOpenFDQueue.fulfilled, (s, a) => { s.queueLoading = false; s.queue = a.payload?.Data || []; })
            .addCase(fetchVerifyOpenFDQueue.rejected, (s, a) => { s.queueLoading = false; s.queueError = a.payload; s.queue = []; });

        builder
            .addCase(fetchVerifyOpenFDDetail.pending, (s) => { s.detailLoading = true; s.detailError = null; s.detail = null; })
            .addCase(fetchVerifyOpenFDDetail.fulfilled, (s, a) => { s.detailLoading = false; s.detail = a.payload?.Data || null; })
            .addCase(fetchVerifyOpenFDDetail.rejected, (s, a) => { s.detailLoading = false; s.detailError = a.payload; s.detail = null; });

        builder
            .addCase(fetchOpenFDBanks.fulfilled, (s, a) => { s.banks = a.payload?.Data || []; })
            .addCase(fetchOpenFDBanks.rejected, (s) => { s.banks = []; });

        builder
            .addCase(submitApproveOpenFD.pending, (s) => { s.verifyLoading = true; })
            .addCase(submitApproveOpenFD.fulfilled, (s) => { s.verifyLoading = false; })
            .addCase(submitApproveOpenFD.rejected, (s) => { s.verifyLoading = false; });

        builder
            .addCase(submitUpdateOpenFD.pending, (s) => { s.updateLoading = true; })
            .addCase(submitUpdateOpenFD.fulfilled, (s) => { s.updateLoading = false; })
            .addCase(submitUpdateOpenFD.rejected, (s) => { s.updateLoading = false; });
    },
});

export const { clearVerifyOpenFDDetail, resetVerifyOpenFD } = verifyOpenFDSlice.actions;

export const selectVOFQueue = (s) => s.verifyOpenFD.queue;
export const selectVOFQueueLoading = (s) => s.verifyOpenFD.queueLoading;
export const selectVOFQueueError = (s) => s.verifyOpenFD.queueError;
export const selectVOFDetail = (s) => s.verifyOpenFD.detail;
export const selectVOFDetailLoading = (s) => s.verifyOpenFD.detailLoading;
export const selectVOFDetailError = (s) => s.verifyOpenFD.detailError;
export const selectVOFBanks = (s) => s.verifyOpenFD.banks;
export const selectVOFVerifyLoading = (s) => s.verifyOpenFD.verifyLoading;
export const selectVOFUpdateLoading = (s) => s.verifyOpenFD.updateLoading;

export default verifyOpenFDSlice.reducer;
